import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { shopkeepersAPI } from '../api';
import QRCard from '../components/QRCard';
import type { Shopkeeper } from '../types/shopkeepers';
import { 
  CheckCircleIcon, 
  ArrowRightIcon, 
  BuildingStorefrontIcon 
} from '@heroicons/react/24/outline';

const RegistrationSuccess: React.FC = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<Shopkeeper | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const response = await shopkeepersAPI.getProfile();
      setProfile(response);
    } catch (err) {
      console.error('Failed to load profile for success page');
      // Fallback to dashboard if profile load fails
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleGoToDashboard = () => {
    navigate('/dashboard');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Preparing your shop...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 flex flex-col items-center">
      <div className="max-w-2xl w-full space-y-8">
        {/* Success Header */}
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-green-100 mb-6">
            <CheckCircleIcon className="h-12 w-12 text-green-600" aria-hidden="true" />
          </div>
          <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
            Welcome to Prindox!
          </h2>
          <p className="mt-4 text-xl text-gray-600">
            Your shop <span className="font-bold text-blue-600">"{profile?.shop_name}"</span> is successfully registered.
          </p>
        </div>

        {/* Action Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="p-8">
            <h3 className="text-lg font-medium text-gray-900 mb-4 text-center">
              Your Shop QR Code
            </h3>
            <p className="text-gray-500 text-center mb-8 max-w-md mx-auto">
              Download and print this QR code now. Place it in your shop so customers can instantly scan and upload documents.
            </p>

            {/* QR Code Component */}
            {profile && (
              <div className="flex justify-center mb-8">
                <div className="w-full max-w-sm">
                  <QRCard shopId={profile.public_id} shopName={profile.shop_name} />
                </div>
              </div>
            )}

            {/* Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8 pt-8 border-t border-gray-100">
               <button
                onClick={handleGoToDashboard}
                className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-4 border border-transparent text-lg font-medium rounded-xl text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-lg transition-transform active:scale-95"
              >
                <BuildingStorefrontIcon className="mr-2 h-6 w-6" />
                Go to Dashboard
              </button>
            </div>
            
            <p className="text-xs text-gray-400 text-center mt-6">
                You can always access this QR code later from your Profile.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistrationSuccess;
